/**
 * @description URL 筛选器参数解析工具
 * 用于解析从 CasualGenreProfile 页面跳转时携带的 URL 参数
 */

import moment from 'moment';
import { buildSilenceExecutor } from '@home/components/AINavigation/utils/formatters';
import { isEmpty, isNotEmpty } from '@intelligence/v3/utils/common';
import { getNullableMoment } from '@intelligence/v3/utils/date';
import { handleMetricRangeFilter as handleMetricRangeFilterOriginal } from '@intelligence/utils/filterUtils';
import { REVENUE_SCOPE_VALUES, getAdjustedRevenueValue } from '@intelligence/utils/revenueScope';
import { formatGenerePageFunc, formatRevenueRangePageFunc } from '@intelligence/components/Filter/util';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import {
  allMetricsOptions,
  getSourceMetricsByValue,
  getMetricOptionByValue,
  defaultDataSourceList,
  defaultMetricTypeList,
} from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';

import type { MainGenre, SubGenre } from '@intelligence/components/GenreSelectMiddleware/types';
import type { IOptionWithChildren } from '@intelligence/components/GenreSelect/types';

const splitStringCodes = (str?: string): string[] => {
  if (typeof str !== 'string') {
    return [];
  }

  const codes = str
    .split(',')
    .map((item) => item.trim())
    .filter(isNotEmpty);

  // 去重处理
  return Array.from(new Set(codes));
};

/**
 * 解析逗号分隔的数组参数
 * @param param - URL 参数值（可能是字符串或数组）
 * @returns 解析后的字符串数组
 */
const parseArrayParam = (param: string | string[] | undefined): string[] => {
  if (!param) {
    return [];
  }

  let result: string[];

  if (Array.isArray(param)) {
    result = param.flatMap((item) => {
      return splitStringCodes(item);
    });
  } else {
    result = splitStringCodes(param);
  }

  return result;
};

const PLATFORM_CASCADER_ALL_VALUE_MAP: IRecord<string[]> = {
  mscience: ['PC', 'PlayStation', 'Xbox', 'Nintendo'],
  gsd: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'],
  gamalytic: ['PC'],
  vginsights: ['PC'],
  npd: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch', 'WII U'],
  ampere: ['PC', 'PlayStation', 'Xbox'],
  newzoo: ['PC', 'PlayStation 4', 'PlayStation 5', 'Xbox One', 'Xbox Series X|S', 'Nintendo Switch'],
};

const SEGMENT_CASCADER_ALL_VALUE_MAP: IRecord<string[]> = {
  mscience: [
    'Full Game',
    'MTX',
    'DLC: All Other',
    'DLC: Expansion',
    'DLC: Content Pre-Purchase Pass',
    'DLC: Unlockable Content Pass',
    'Subscription',
  ],
  gsd: ['Full Game', 'In-Game Currency'],
};

/**
 * 解析数组类型的筛选器参数
 * @param params - URL query 参数对象
 * @param paramKey - URL 参数键
 * @param filterKey - 筛选器键
 * @returns 筛选器参数对象
 */
const parseArrayUrlFilterParams = (params: IRecord, paramKey: string, filterKey: string): IRecord => {
  const result: IRecord = {};
  const values = parseArrayParam(params?.[paramKey]);

  if (values.length > 0) {
    result[filterKey] = { value: values };
  }

  return result;
};

/**
 * 解析级联筛选器参数的通用方法
 * @param options - 配置选项
 * @param options.paramKey - URL 参数键 (如 'cascader_platform', 'cascader_segment')
 * @param options.filterKey - 筛选器键 (如 filterKeyMap.PLATFORMCASCADER)
 * @param options.params - URL query 参数对象（可选）
 * @param options.separator - 分隔符，默认为 '$$'
 * @returns 级联筛选器参数对象 { filterKey: { value: { source: [values] } } }
 *
 * @example
 * // 输入: { paramKey: 'cascader_platform', filterKey: filterKeyMap.PLATFORMCASCADER, params }
 * // 输入值: { cascader_platform: 'mscience$$PC,mscience$$PlayStation' }
 * // 输出: { platform_cascader: { value: { mscience: ['PC', 'PlayStation'] } } }
 */
const parseCascaderUrlFilterParams = (options: {
  paramKey: string;
  filterKey: string;
  params?: IRecord;
  separator?: string;
  fallbackValueMap?: IRecord<string[]>;
  dataSourceNormalizer?: (dataSource: string) => string;
}): IRecord => {
  const { paramKey, filterKey, params, separator = '$$', fallbackValueMap, dataSourceNormalizer } = options;
  const paramValue = params?.[paramKey];

  if (isEmpty(paramValue)) {
    return {};
  }

  const resultMap: IRecord<string[]> = {};
  const items = parseArrayParam(paramValue);

  for (const item of items) {
    const [dataSourceRaw, valueRaw] = item.split(separator);
    const dataSource = dataSourceNormalizer ? dataSourceNormalizer(dataSourceRaw || '') : dataSourceRaw;

    let nextValues: string[] = [];

    if (dataSource && valueRaw) {
      if (isNotEmpty(valueRaw)) {
        nextValues = [valueRaw];
      }
    } else if (dataSource && !item.includes(separator) && fallbackValueMap?.[dataSource]) {
      nextValues = fallbackValueMap[dataSource];
    }

    if (!dataSource || nextValues.length === 0) {
      continue;
    }

    if (!resultMap[dataSource]) {
      resultMap[dataSource] = [];
    }

    resultMap[dataSource] = Array.from(new Set([...resultMap[dataSource], ...nextValues]));
  }

  if (isEmpty(resultMap)) {
    return {};
  }

  return { [filterKey]: { value: resultMap } };
};

// Range 参数配置映射
const rangeConfigMap = [
  // 手游通用榜单页
  {
    paramKey: 'au_range',
    filterKey: filterKeyMap.ACTIVE_USERS_RANGE,
  },
  {
    paramKey: 'revenue_range',
    filterKey: filterKeyMap.REVENUE_RANGE,
  },
  {
    paramKey: 'arpu_range',
    filterKey: filterKeyMap.ARPU_RANGE,
  },
  {
    paramKey: 'downloads_range',
    filterKey: filterKeyMap.DOWNLOADS_RANGE,
  },
  // pcConsole榜单页
  {
    paramKey: 'pcu_range',
    filterKey: filterKeyMap.PCU_RANGE,
  },
  {
    paramKey: 'npd_physical_revenue_range',
    filterKey: filterKeyMap.PHYSICAL_RANGE,
  },
  {
    paramKey: 'mscience_digital_revenue_range',
    filterKey: filterKeyMap.DIGITAL_RANGE,
  },
  {
    paramKey: 'steam_price_range',
    filterKey: filterKeyMap.PRICE_RANGE,
  },
  {
    paramKey: 'steam_premium_revenue_range',
    filterKey: filterKeyMap.PREMIUM_RANGE,
  },
];

/**
 * 解析 Range 类型的筛选器参数
 * @param params - URL query 参数对象
 * @returns Range 筛选器参数对象
 */
const parseRangeUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  for (const { paramKey, filterKey } of rangeConfigMap) {
    const min = params?.[`${paramKey}_min`];
    const max = params?.[`${paramKey}_max`];

    // 验证是否为有效数值字符串
    const minNum = min !== undefined ? Number(min) : undefined;
    const maxNum = max !== undefined ? Number(max) : undefined;

    const isValidMin = min !== undefined && !isNaN(minNum as number);
    const isValidMax = max !== undefined && !isNaN(maxNum as number);

    if (isValidMin || isValidMax) {
      const rangeValue = {
        value: {
          min: isValidMin ? minNum : undefined,
          max: isValidMax ? maxNum : undefined,
        },
      };
      result[filterKey] = formatRevenueRangePageFunc(rangeValue as any, filterKey);
    }
  }

  return result;
};

/**
 * 解析排序相关参数
 * @param params - URL query 参数对象
 * @returns 排序参数对象 { order_source, order_metric, order_mode, order }
 */
const parseOrderUrlParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  const orderSource = params?.order_source;
  if (orderSource) {
    result.order_source = String(orderSource);
  }

  const orderMetric = params?.order_metric;
  if (orderMetric) {
    result.order_metric = String(orderMetric);
  }

  const orderMode = params?.order_mode;
  if (orderMode !== undefined) {
    result.ratio_type = Number(orderMode);
  }

  const order = params?.order;
  if (order) {
    result.order = String(order);
  }

  return result;
};

// Range API 参数配置映射
const rangeApiConfigMap = [
  {
    paramKey: 'au_range',
    apiMinKey: 'au_min',
    apiMaxKey: 'au_max',
  },
  {
    paramKey: 'revenue_range',
    apiMinKey: 'revenue_min',
    apiMaxKey: 'revenue_max',
  },
  {
    paramKey: 'arpu_range',
    apiMinKey: 'arpu_min',
    apiMaxKey: 'arpu_max',
  },
  {
    paramKey: 'downloads_range',
    apiMinKey: 'downloads_min',
    apiMaxKey: 'downloads_max',
  },
];

/**
 * 解析 Range 筛选器参数为 API 可用格式
 * @param params - URL query 参数对象
 * @returns API 可用的 Range 参数对象 { au_min, au_max, downloads_min, downloads_max }
 */
const parseRangeApiParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  for (const { paramKey, apiMinKey, apiMaxKey } of rangeApiConfigMap) {
    const min = params?.[`${paramKey}_min`];
    const max = params?.[`${paramKey}_max`];

    // 验证是否为有效数值字符串
    if (min !== undefined && !isNaN(Number(min))) {
      result[apiMinKey] = Number(min);
    }
    if (max !== undefined && !isNaN(Number(max))) {
      result[apiMaxKey] = Number(max);
    }
  }

  return result;
};

/**
 * 解析地区国家筛选器参数
 * @param params - URL query 参数对象
 * @returns 地区国家筛选器参数对象 { region: { type, regions, markets } }
 */
const parseRegionUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  const markets = parseArrayParam(params?.region);
  if (markets.length === 0) {
    return result;
  }

  // 构建完整的地区参数结构，以匹配 getCoucountriesExtendValue 期望的格式
  result[filterKeyMap.REGION] = {
    type: 'market',
    regions: [],
    markets,
    filterType: 'market',
    filterRegions: [],
    filterMarkets: markets,
    region_type: 'market',
    realMarkets: markets,
  };

  return result;
};

/**
 * 解析日期筛选器参数（包括粒度和日期范围）
 * 统一处理 granularity、start_date、end_date 参数
 * 此函数替换原有的 parseGranularityParams 函数
 * @param params - URL query 参数对象
 * @returns { filterParams: IRecord, customParams: IRecord }
 *
 * URL 参数格式：
 * - granularity: 日期粒度 ("daily" | "monthly")
 * - start_date: 开始日期 ("YYYY-MM-DD")
 * - end_date: 结束日期 ("YYYY-MM-DD")
 */
const parseDateRangeUrlFilterParamsOriginal = (
  params: IRecord,
): {
  filterParams: IRecord;
  customParams: IRecord;
} => {
  const filterParams: IRecord = {};
  const customParams: IRecord = {};

  const granularity = params?.granularity || 'daily' as string | undefined;
  const startDate = params?.start_date;
  const endDate = params?.end_date;

  // 验证 granularity 必须为 "daily"、"weekly"、"monthly" 之一
  const validGranularities = ['daily', 'weekly', 'monthly'];
  const isValidGranularity = granularity !== undefined && validGranularities.includes(granularity);

  // 验证 startDate 和 endDate 同时为有效日期
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);
  const isValidDateRange = startDateM !== null && endDateM !== null;

  // 只有当 granularity 有效且日期范围有效时，才返回数据
  if (isValidGranularity && isValidDateRange) {
    const dateTypeMap: IRecord<moment.unitOfTime.StartOf> = {
      daily: 'date',
      weekly: 'week',
      monthly: 'month',
    };
    const dateType = dateTypeMap[granularity];
    startDateM?.startOf(dateType);
    endDateM?.endOf(dateType);
    // 构建筛选器参数（用于 Filter 组件显示）
    filterParams[filterKeyMap.RANGE_TIME] = {
      type: dateType,
      value: [startDateM, endDateM],
    };

    // 构建 API 参数（date_type、start_date、end_date 用于 API 调用）
    customParams.date_type = String(granularity);
    customParams.start_date = String(startDateM.format('YYYY-MM-DD'));
    customParams.end_date = String(endDateM.format('YYYY-MM-DD'));
  }

  return { filterParams, customParams };
};

/**
 * 解析发布时间筛选器参数
 * @param params - URL query 参数对象
 * @returns { filterParams: IRecord, customParams: IRecord }
 *
 * URL 参数格式：
 * - release_start_date: 发布开始日期 ("YYYY-MM-DD")
 * - release_end_date: 发布结束日期 ("YYYY-MM-DD")
 */
const parseReleaseTimeUrlFilterParamsOriginal = (
  params: IRecord,
): {
  filterParams: IRecord;
  customParams: IRecord;
} => {
  const filterParams: IRecord = {};
  const customParams: IRecord = {};

  const startDate = params?.release_start_date;
  const endDate = params?.release_end_date;

  // 验证 startDate 和 endDate 同时为有效日期
  const startDateM = getNullableMoment(startDate);
  const endDateM = getNullableMoment(endDate);
  const isValidDateRange = startDateM !== null && endDateM !== null;

  // 只有当日期范围有效时，才返回数据
  if (isValidDateRange) {
    const startDateNew = startDateM.format('YYYY-MM-DD');
    const endDateNew = endDateM.format('YYYY-MM-DD');
    // 构建筛选器参数（用于 Filter 组件显示）
    // 同时包含 value 和 start_release_time/end_release_time 属性，以兼容 API 处理逻辑
    filterParams[filterKeyMap.RELEASE_TIME] = {
      value: [startDateNew, endDateNew],
      start_release_time: startDateNew,
      end_release_time: endDateNew,
    };

    // 构建 API 参数（API 期望的参数名是 start_release_time 和 end_release_time）
    customParams.start_release_time = startDateNew;
    customParams.end_release_time = endDateNew;
  }

  return { filterParams, customParams };
};

/**
 * 解析 Platform 筛选器参数
 * @param params - URL query 参数对象
 * @returns Platform 筛选器参数对象
 */
const parsePlatformUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseArrayUrlFilterParams(params, 'platform', filterKeyMap.PLATFORM);
};

/**
 * 解析级联 Platform 筛选器参数
 * URL 参数格式：cascader_platform=mscience$$PC,mscience$$PlayStation,gsd$$PlayStation 5
 * 或：cascader_platform=mscience（表示选中该数据源下的全部子平台）
 * @param params - URL query 参数对象
 * @returns Platform 筛选器参数对象
 */
const parseCascaderPlatformUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseCascaderUrlFilterParams({
    paramKey: 'cascader_platform',
    filterKey: filterKeyMap.PLATFORMCASCADER,
    params,
    fallbackValueMap: PLATFORM_CASCADER_ALL_VALUE_MAP,
    dataSourceNormalizer: (dataSource: string) => dataSource.toLowerCase(),
  });
};

/**
 * 解析 Company 筛选器参数
 * @param params - URL query 参数对象
 * @returns Company 筛选器参数对象
 */
const parseCompanyUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseArrayUrlFilterParams(params, 'company', filterKeyMap.ALL_COMPANYS);
};

/**
 * 解析 Sensor Tower Tag 和 App Magic Tag 筛选器参数
 * @param params - URL query 参数对象
 * @returns Tag 筛选器参数对象
 */
const parseTagUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  // Tag 参数映射配置
  const tagKeyMap: IRecord<string> = {
    // SensorTower Tags
    art_style: filterKeyMap.SENSOR_TOWER_TAG_ART_STYLE,
    camera_pov: filterKeyMap.SENSOR_TOWER_TAG_CAMERA_POV,
    product_model: filterKeyMap.SENSOR_TOWER_TAG_PRODUCT_MODEL,
    setting: filterKeyMap.SENSOR_TOWER_TAG_SETTING,
    tag: filterKeyMap.SENSOR_TOWER_TAG_TAGS,
    theme: filterKeyMap.SENSOR_TOWER_TAG_THEME,
    monetization: filterKeyMap.SENSOR_TOWER_TAG_MONETIZATION,
    // App Magic Tags
    appmagic_themes: filterKeyMap.APPMAGIC_TAG_THEMES,
    appmagic_features: filterKeyMap.APPMAGIC_TAG_FEATURES,
    appmagic_ip: filterKeyMap.APPMAGIC_TAG_IP,
    appmagic_settings: filterKeyMap.APPMAGIC_TAG_SETTINGS,
    appmagic_art_styles: filterKeyMap.APPMAGIC_TAG_ART_STYLES,
  };

  for (const [urlKey, filterKey] of Object.entries(tagKeyMap)) {
    let urlValue = params?.[urlKey];

    // theme 参数需要特殊处理，将 $$ 替换为 @
    if (urlKey === 'theme' && urlValue) {
      urlValue = urlValue.replace(/\$\$/g, '@');
    }

    const values = parseArrayParam(urlValue);
    if (values.length > 0) {
      result[filterKey] = { value: values };
    }
  }

  return result;
};

/**
 * 解析 Growth 类型筛选器参数
 * URL 参数格式：{metric}_growth_compare_opt=>=, {metric}_growth_compare_value=500000
 *
 * @param growthPrefixes - Growth 类型筛选器的前缀数组，默认为 ['revenue']
 * @param params - URL query 参数对象
 * @returns Growth 类型筛选器参数对象
 *
 * @example
 * // 输入: ['revenue'], { revenue_growth_compare_opt: '>=', revenue_growth_compare_value: '500000' }
 * // 输出: { revenueGrowth: { value: { opt: '>=', value: 500000 } } }
 */
const parseGrowthUrlFilterParamsOriginal = (growthPrefixes: string[] = ['revenue'], params: IRecord): IRecord => {
  const result: IRecord = {};

  // Growth 类型筛选器 key 映射配置
  const growthKeyMap: IRecord<string> = {
    revenue: filterKeyMap.REVENUE_GROWTH,
    // 未来可添加：
    // au: filterKeyMap.ACTIVE_USERS_GROWTH,
    // downloads: filterKeyMap.DOWNLOADS_GROWTH,
  };

  for (const prefix of growthPrefixes) {
    const optKey = `${prefix}_growth_compare_opt`;
    const valueKey = `${prefix}_growth_compare_value`;

    const opt = params?.[optKey];
    const value = params?.[valueKey];
    const numValue = Number.parseFloat(value);

    if (opt && value !== undefined && value !== null && !Number.isNaN(numValue)) {
      const filterKey = growthKeyMap[prefix];

      // 验证运算符
      if (['<', '<=', '>', '>=', '='].includes(String(opt))) {
        result[filterKey] = {
          value: {
            opt,
            value: numValue,
          },
        };
      }
    }
  }

  return result;
};

const parseRelationalListApiParams = (relationKey: string, listKey: string, params: IRecord): IRecord => {
  const result: IRecord = {};

  const relation = params?.[relationKey] || 'or';
  const engineStr = params?.[listKey];

  if (engineStr) {
    result[relationKey] = ['and', 'or'].includes(relation) ? relation : 'or';
    result[listKey] = parseArrayParam(engineStr);
  }

  return result;
};

/**
 * 解析 Game Engine 筛选器参数为 API 可用格式
 * URL 参数格式：game_engine_relation=or, game_engine=Abyss Engine,Adobe AIR
 *
 * @param params - URL query 参数对象
 * @returns API 可用的 Game Engine 参数对象 { game_engine, game_engine_relation }
 */
const parseGameEngineApiParamsOriginal = (params: IRecord): IRecord => {
  return parseRelationalListApiParams('game_engine_relation', 'game_engine', params);
};

/**
 * 解析 Game Engine 筛选器参数
 * URL 参数格式：game_engine_relation=or, game_engine=Abyss Engine,Adobe AIR
 *
 * @param params - URL query 参数对象
 * @returns Game Engine 筛选器参数对象
 *
 * @example
 * // 输入: { game_engine_relation: 'or', game_engine: 'Abyss Engine,Adobe AIR' }
 * // 输出: { gameEngine: { value: ['Abyss Engine', 'Adobe AIR'], logic: 'or' } }
 */
const parseGameEngineUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  const relation = params?.game_engine_relation;
  const engineStr = params?.game_engine;

  if (!engineStr) {
    return result;
  }

  const engineList = parseArrayParam(engineStr);

  if (engineList.length > 0) {
    result[filterKeyMap.GAME_ENGINE] = {
      logic: relation === 'and' ? 'AND' : 'OR',
      value: engineList,
    };
  }

  return result;
};

/**
 * 解析 Steam Tags 筛选器API 可用格式
 * URL 参数格式：steam_tag_relation=or, steam_tag=Abyss Engine,Adobe AIR
 *
 * @param params - URL query 参数对象
 * @returns API 可用的 Steam Tags 参数对象 { steam_tag, steam_tag_relation }
 */
const parseSteamTagsApiParamsOriginal = (params: IRecord): IRecord => {
  return parseRelationalListApiParams('steam_tag_relation', 'steam_tag', params);
};

/**
 * 解析 Steam Tags 筛选器参数
 * URL 参数格式：steam_tag=1980s,2.5D, steam_tag_relation=and|or
 *
 * @param params - URL query 参数对象
 * @returns Steam Tags 筛选器参数对象
 *
 * @example
 * // 输入: { steam_tag: '1980s,2.5D', steam_tag_relation: 'or' }
 * // 输出: { steam_tag: { value: ['1980s', '2.5D'], logic: 'OR' } }
 */
const parseSteamTagsUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};
  const relation = params?.steam_tag_relation;
  const steamTags = parseArrayParam(params?.steam_tag);

  if (steamTags.length > 0) {
    result[filterKeyMap.STEAM_TAGS] = {
      logic: relation === 'and' ? 'AND' : 'OR',
      value: steamTags,
    };
  }

  return result;
};

/**
 * 解析 GSD Publisher Shared 筛选器参数
 * URL 参数格式：gsd_publisher_shared=all|NETWORK|RETAIL
 *
 * @param params - URL query 参数对象
 * @returns GSD Publisher Shared 筛选器参数对象
 *
 * @example
 * // 输入: { gsd_publisher_shared: 'RETAIL' }
 * // 输出: { publisher: { value: 'RETAIL' } }
 */
const parseGSDPublisherSharedUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};
  const publisherValue = params?.gsd_publisher_shared;

  // 验证是否为有效值
  const validValues = ['all', 'NETWORK', 'RETAIL'];
  if (publisherValue && validValues.includes(String(publisherValue))) {
    result[filterKeyMap.PUBLISHER] = {
      value: String(publisherValue),
    };
  }

  return result;
};

/**
 * 解析 Segment for Sales 筛选器参数
 * URL 参数格式：cascader_segment=mscience$$Base Game,gsd$$In-Game
 * 或：cascader_segment=mscience（表示选中该数据源下的全部子项）
 *
 * @param params - URL query 参数对象
 * @returns Segment for Sales 筛选器参数对象
 */
const parseSegmentUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseCascaderUrlFilterParams({
    paramKey: 'cascader_segment',
    filterKey: filterKeyMap.CASCADER,
    params,
    fallbackValueMap: SEGMENT_CASCADER_ALL_VALUE_MAP,
    dataSourceNormalizer: (dataSource: string) => dataSource.toLowerCase(),
  });
};

/**
 * 解析 Game Mode 筛选器参数
 * URL 参数格式：game_mode=Cross-Platform Multiplayer,LAN Co-op
 *
 * @param params - URL query 参数对象
 * @returns Game Mode 筛选器参数对象
 *
 * @example
 * // 输入: { game_mode: 'Cross-Platform Multiplayer,LAN Co-op' }
 * // 输出: { game_mode: { value: ['Cross-Platform Multiplayer', 'LAN Co-op'] } }
 */
const parseGameModeUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseArrayUrlFilterParams(params, 'game_mode', filterKeyMap.GAME_MODE);
};

/**
 * 解析 DataSource（单选）筛选器参数
 * URL 参数格式：dataSource=sensortower 或 dataSource=appannie
 *
 * @param params - URL query 参数对象
 * @returns DataSource 筛选器参数对象
 *
 * @example
 * // 输入: { dataSource: 'sensortower' }
 * // 输出: { dataSourceSingle: { value: 'sensortower' } }
 */
const parseMobileDataSourceUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};
  const dataSourceValue = splitStringCodes(params?.dataSource)[0] || '';

  // 验证是否为有效值
  const validValues = ['sensortower', 'appannie'];
  if (dataSourceValue && validValues.includes(dataSourceValue)) {
    result[filterKeyMap.DATASOURCESIM] = {
      value: dataSourceValue,
    };
  }

  return result;
};

/**
 * 解析收入范围筛选器参数
 * URL 参数格式：revenue_scope=excluding_store_fees 或 including_store_fees
 *
 * @param params - URL query 参数对象
 * @returns 收入范围筛选器参数对象
 */
const parseRevenueScopeUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};
  const revenueScopeValue = splitStringCodes(params?.[filterKeyMap.REVENUE_SCOPE])[0] || '';
  const validValues = Object.values(REVENUE_SCOPE_VALUES);

  if (validValues.includes(revenueScopeValue as any)) {
    result[filterKeyMap.REVENUE_SCOPE] = {
      value: revenueScopeValue,
    };
  }

  return result;
};

/**
 * 解析收入范围筛选器 API 参数
 * URL 参数格式：revenue_scope=excluding_store_fees 或 including_store_fees
 *
 * @param params - URL query 参数对象
 * @returns 收入范围 API 参数对象
 */
const parseRevenueScopeApiParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};
  const revenueScopeValue = splitStringCodes(params?.[filterKeyMap.REVENUE_SCOPE])[0] || '';
  const validValues = Object.values(REVENUE_SCOPE_VALUES);

  if (validValues.includes(revenueScopeValue as any)) {
    result[filterKeyMap.REVENUE_SCOPE] = revenueScopeValue;
    result.adjusted_revenue = getAdjustedRevenueValue(revenueScopeValue);
  }

  return result;
};

/**
 * 解析 Retention 筛选器参数
 * URL 参数格式：retention=D3,D7,D15
 *
 * @param params - URL query 参数对象
 * @returns Retention 筛选器参数对象
 *
 * @example
 * // 输入: { retention: 'D3,D7,D15' }
 * // 输出: { retention: { value: ['D3', 'D7', 'D15'] } }
 */
const parseRetentionUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  return parseArrayUrlFilterParams(params, 'retention', filterKeyMap.RETENTION);
};

/**
 * 品类参数序列化时使用的分隔符
 */
const GENRE_JOINER = '$$';

/**
 * 递归获取某个品类的所有子品类（包括孙品类）
 * @param option 品类选项数据
 * @param level 需要获取的层级深度（1=直接子品类，2=子品类的子品类，以此类推）
 * @returns 所有子品类的 value 数组（扁平化）
 */
const getAllChildValues = (option: IOptionWithChildren | undefined, level: number): string[] => {
  if (!option || level <= 0) return [];
  const result: string[] = [];
  const children = option.children || [];

  for (const child of children) {
    if (child?.value) {
      result.push(child.value);
    }
    if (level > 1) {
      result.push(...getAllChildValues(child, level - 1));
    }
  }

  return result;
};

/**
 * 从品类选项中查找指定 value 的品类选项
 * @param options 品类选项数组
 * @param value 品类 value
 * @returns 匹配的品类选项
 *
 * 【注意】只在当前层级查找，不递归到子层级
 * 原因：所有调用场景都是分层查找（一级在顶层、二级在一级children中...）
 * 如果递归查找，可能误匹配到其他层级的同名项（如 iegg 的 Action 下有 Adventure 子品类，
 * 同时顶层也有独立的一级品类 Adventure，递归会先匹配到子层级的 Adventure）
 */
const findOptionByValue = (options: IOptionWithChildren[], value: string): IOptionWithChildren | undefined => {
  return options.find((option) => option?.value === value);
};

// ========== 原方法（已弃用）==========
// 原方法使用递归深度优先搜索，会在所有层级中查找匹配项
// 问题：如果目标层级存在同名值，会错误地返回更深层级的结果
// 例如：iegg 的 Action 一级品类下有 'Adventure' 子品类，同时顶层也有独立的 'Adventure' 一级品类
// 递归查找 'Adventure' 时，会先找到 Action 下的子品类，而不是顶层的一级品类
//
// const findOptionByValue = (options: IOptionWithChildren[], value: string): IOptionWithChildren | undefined => {
//   for (const option of options) {
//     if (option?.value === value) {
//       return option;
//     }
//     if (option?.children) {
//       const found = findOptionByValue(option.children, value);
//       if (found) return found;
//     }
//   }
//   return undefined;
// };

/**
 * 解析品类相关的 URL 参数
 * @param params - URL query 参数对象
 * @param options - 其他参数
 * @param options.genreOptionsMap - 品类选项数据map
 * @returns 品类筛选器参数对象 { genretor: { source, mainGenres, subGenres, level3Genres, level4Genres } }
 *
 * URL 参数格式：
 * - genre_source: 数据源 (如 'iegg', 'igdb', 'sensortower', 'appmagic')
 * - genre_main_genres: 主品类，多个用逗号分隔，格式为 "relation$$value"
 *   例如: "or$$RPG,and$$Strategy"
 * - genre_sub_genres: 子品类，多个用逗号分隔，格式为 "parentValue$$value"
 *   例如: "RPG$$ActionRPG,Strategy$$TurnBasedStrategy"
 * - genre_level3_genres: 第3层品类（App Magic 使用），格式为 "superGenre$$majorCategory$$midCategory"
 *   例如: "Casual$$Hypercasual$$Arcade"
 * - genre_level4_genres: 第4层品类（App Magic 使用），格式为 "superGenre$$majorCategory$$midCategory$$subCategory"
 *   例如: "Casual$$Hypercasual$$Arcade$$Runner"
 *
 * 推导规则：
 * 当某个层级的品类被选中但下一层级未指定时，递归推导所有子层级的所有子品类
 */
export const parseGenreUrlFilterParamsOriginal = (
  params: IRecord,
  options: {
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  },
): IRecord => {
  const { genreOptionsMap } = options || {};
  const result: IRecord = {};

  // 初始化 genretor 对象
  const initGenretor = () => {
    result.genretor = {
      source: '',
      mainGenres: [],
      subGenres: [],
      level3Genres: [],
      level4Genres: [],
      level5Genres: [],
    };
  };

  const genreSource = params?.genre_source;
  if (!genreSource) {
    return result;
  }

  initGenretor();
  result.genretor.source = String(genreSource);

  const currentOptions = genreOptionsMap?.[genreSource] || [];

  // ========== 第1步：解析显式指定的品类参数 ==========

  // 解析 genre_main_genres
  const genreMainGenres = params?.genre_main_genres;
  if (genreMainGenres && typeof genreMainGenres === 'string') {
    const mainGenres: MainGenre[] = genreMainGenres
      .split(',')
      .filter(Boolean)
      .map((item) => {
        const [relation, value] = item.split(GENRE_JOINER);
        if (!value) return null;
        return {
          relation: (relation || 'or') as 'or' | 'and',
          value,
          checked: false,
          indeterminate: false,
        } as unknown as MainGenre;
      })
      .filter((item): item is MainGenre => item !== null);
    result.genretor.mainGenres = mainGenres;
  }

  // 解析 genre_sub_genres（显式指定的子品类）
  const genreSubGenres = params?.genre_sub_genres;
  const explicitSubGenres: SubGenre[] = [];
  if (genreSubGenres && typeof genreSubGenres === 'string') {
    explicitSubGenres.push(
      ...genreSubGenres
        .split(',')
        .filter(Boolean)
        .map((item) => {
          const [parentValue, value] = item.split(GENRE_JOINER);
          if (!value || !parentValue) return null;
          return {
            parentValue,
            value,
            checked: true,
          } as unknown as SubGenre;
        })
        .filter((item): item is SubGenre => item !== null),
    );
  }

  // 解析 genre_level3_genres（显式指定的第3层品类）
  const genreLevel3Genres = params?.genre_level3_genres;
  const explicitLevel3Genres: SubGenre[] = [];
  if (genreLevel3Genres && typeof genreLevel3Genres === 'string') {
    explicitLevel3Genres.push(
      ...genreLevel3Genres
        .split(',')
        .filter(Boolean)
        .map((item) => {
          const parts = item.split(GENRE_JOINER);
          const [superGenre, majorCategory, midCategory] = parts;
          if (!superGenre || !majorCategory || !midCategory) return null;
          return {
            parentValue: superGenre,
            parent2Value: majorCategory,
            value: midCategory,
            checked: true,
          } as unknown as SubGenre;
        })
        .filter((item): item is SubGenre => item !== null),
    );
  }

  // 解析 genre_level4_genres（显式指定的第4层品类）
  const genreLevel4Genres = params?.genre_level4_genres;
  const explicitLevel4Genres: SubGenre[] = [];
  if (genreLevel4Genres && typeof genreLevel4Genres === 'string') {
    explicitLevel4Genres.push(
      ...genreLevel4Genres
        .split(',')
        .filter(Boolean)
        .map((item) => {
          const parts = item.split(GENRE_JOINER);
          const [superGenre, majorCategory, midCategory, subCategory] = parts;
          if (!superGenre || !majorCategory || !midCategory || !subCategory) return null;
          return {
            parentValue: superGenre,
            parent2Value: majorCategory,
            parent3Value: midCategory,
            value: subCategory,
            checked: true,
          } as unknown as SubGenre;
        })
        .filter((item): item is SubGenre => item !== null),
    );
  }

  // 解析 genre_level5_genres（显式指定的第5层品类）
  const genreLevel5Genres = params?.genre_level5_genres;
  const explicitLevel5Genres: SubGenre[] = [];
  if (genreLevel5Genres && typeof genreLevel5Genres === 'string') {
    explicitLevel5Genres.push(
      ...genreLevel5Genres
        .split(',')
        .filter(Boolean)
        .map((item) => {
          const parts = item.split(GENRE_JOINER);
          const [superGenre, majorCategory, midCategory, subCategory, minorCategory] = parts;
          if (!superGenre || !majorCategory || !midCategory || !subCategory || !minorCategory) return null;
          return {
            parentValue: superGenre,
            parent2Value: majorCategory,
            parent3Value: midCategory,
            parent4Value: subCategory,
            value: minorCategory,
            checked: true,
          } as unknown as SubGenre;
        })
        .filter((item): item is SubGenre => item !== null),
    );
  }

  // ========== 第2步：自动推导未指定下一层级的所有子品类 ==========

  const finalSubGenres: SubGenre[] = [];
  const finalLevel3Genres: SubGenre[] = [];
  const finalLevel4Genres: SubGenre[] = [];
  const finalLevel5Genres: SubGenre[] = [];

  // 遍历所有主品类
  for (const mainGenre of result.genretor.mainGenres) {
    const mainGenreValue = mainGenre?.value;
    if (!mainGenreValue) continue;

    const mainGenreOption = findOptionByValue(currentOptions, mainGenreValue);

    // 检查该主品类是否有显式指定的子品类
    const hasExplicitSubGenres = explicitSubGenres.some((sg) => sg?.parentValue === mainGenreValue);

    if (hasExplicitSubGenres) {
      // 有显式指定的子品类，只添加指定的
      explicitSubGenres
        .filter((sg) => sg?.parentValue === mainGenreValue)
        .forEach((sg) => {
          if (sg?.value) {
            finalSubGenres.push(sg);
          }
        });
    } else {
      // 没有显式指定的子品类，推导所有子品类
      const childValues = getAllChildValues(mainGenreOption, 1);
      for (const childValue of childValues) {
        finalSubGenres.push({
          parentValue: mainGenreValue,
          value: childValue,
          checked: true,
        } as unknown as SubGenre);
      }
    }
  }

  // 遍历所有子品类（包括显式指定和推导的），推导第3层品类
  for (const subGenre of finalSubGenres) {
    const parentValue = subGenre?.parentValue;
    const subGenreValue = subGenre?.value;
    if (!parentValue || !subGenreValue) continue;

    // 查找子品类选项
    const mainGenreOption = findOptionByValue(currentOptions, parentValue);
    const subGenreOption = findOptionByValue(mainGenreOption?.children || [], subGenreValue);

    // 检查该子品类是否有显式指定的第3层品类
    const hasExplicitLevel3Genres = explicitLevel3Genres.some(
      (l3) => l3?.parentValue === parentValue && l3?.parent2Value === subGenreValue,
    );

    if (hasExplicitLevel3Genres) {
      // 有显式指定的第3层品类，只添加指定的
      explicitLevel3Genres
        .filter((l3) => l3?.parentValue === parentValue && l3?.parent2Value === subGenreValue)
        .forEach((l3) => {
          if (l3?.value) {
            finalLevel3Genres.push(l3);
          }
        });
    } else {
      // 没有显式指定的第3层品类，推导所有第3层品类
      const childValues = getAllChildValues(subGenreOption, 1);
      for (const childValue of childValues) {
        finalLevel3Genres.push({
          parentValue,
          parent2Value: subGenreValue,
          value: childValue,
          checked: true,
        } as unknown as SubGenre);
      }
    }
  }

  // 遍历所有第3层品类（包括显式指定和推导的），推导第4层品类
  for (const level3Genre of finalLevel3Genres) {
    const parentValue = level3Genre?.parentValue;
    const parent2Value = level3Genre?.parent2Value;
    const level3GenreValue = level3Genre?.value;
    if (!parentValue || !parent2Value || !level3GenreValue) continue;

    // 查找第3层品类选项
    const mainGenreOption = findOptionByValue(currentOptions, parentValue);
    const subGenreOption = findOptionByValue(mainGenreOption?.children || [], parent2Value);
    const level3GenreOption = findOptionByValue(subGenreOption?.children || [], level3GenreValue);

    // 检查该第3层品类是否有显式指定的第4层品类
    const hasExplicitLevel4Genres = explicitLevel4Genres.some(
      (l4) =>
        l4?.parentValue === parentValue && l4?.parent2Value === parent2Value && l4?.parent3Value === level3GenreValue,
    );

    if (hasExplicitLevel4Genres) {
      // 有显式指定的第4层品类，只添加指定的
      explicitLevel4Genres
        .filter(
          (l4) =>
            l4?.parentValue === parentValue &&
            l4?.parent2Value === parent2Value &&
            l4?.parent3Value === level3GenreValue,
        )
        .forEach((l4) => {
          if (l4?.value) {
            finalLevel4Genres.push(l4);
          }
        });
    } else {
      // 没有显式指定的第4层品类，推导所有第4层品类
      const childValues = getAllChildValues(level3GenreOption, 1);
      for (const childValue of childValues) {
        finalLevel4Genres.push({
          parentValue,
          parent2Value,
          parent3Value: level3GenreValue,
          value: childValue,
          checked: true,
        } as unknown as SubGenre);
      }
    }
  }

  // 遍历所有第4层品类（包括显式指定和推导的），推导第5层品类
  for (const level4Genre of finalLevel4Genres) {
    const parentValue = level4Genre?.parentValue;
    const parent2Value = level4Genre?.parent2Value;
    const parent3Value = level4Genre?.parent3Value;
    const level4GenreValue = level4Genre?.value;
    if (!parentValue || !parent2Value || !parent3Value || !level4GenreValue) continue;

    // 查找第4层品类选项
    const mainGenreOption = findOptionByValue(currentOptions, parentValue);
    const subGenreOption = findOptionByValue(mainGenreOption?.children || [], parent2Value);
    const level3GenreOption = findOptionByValue(subGenreOption?.children || [], parent3Value);
    const level4GenreOption = findOptionByValue(level3GenreOption?.children || [], level4GenreValue);

    // 检查该第4层品类是否有显式指定的第5层品类
    const hasExplicitLevel5Genres = explicitLevel5Genres.some(
      (l5) =>
        l5?.parentValue === parentValue &&
        l5?.parent2Value === parent2Value &&
        l5?.parent3Value === parent3Value &&
        l5?.parent4Value === level4GenreValue,
    );

    if (hasExplicitLevel5Genres) {
      // 有显式指定的第5层品类，只添加指定的
      explicitLevel5Genres
        .filter(
          (l5) =>
            l5?.parentValue === parentValue &&
            l5?.parent2Value === parent2Value &&
            l5?.parent3Value === parent3Value &&
            l5?.parent4Value === level4GenreValue,
        )
        .forEach((l5) => {
          if (l5?.value) {
            finalLevel5Genres.push(l5);
          }
        });
    } else {
      // 没有显式指定的第5层品类，推导所有第5层品类
      const childValues = getAllChildValues(level4GenreOption, 1);
      for (const childValue of childValues) {
        finalLevel5Genres.push({
          parentValue,
          parent2Value,
          parent3Value,
          parent4Value: level4GenreValue,
          value: childValue,
          checked: true,
        } as unknown as SubGenre);
      }
    }
  }

  // ========== 第3步：赋值最终结果 ==========

  result.genretor.subGenres = finalSubGenres;
  result.genretor.level3Genres = finalLevel3Genres;
  result.genretor.level4Genres = finalLevel4Genres;
  result.genretor.level5Genres = finalLevel5Genres;

  // ========== 第4步：计算主品类的 checked/indeterminate 状态 ==========
  // 根据子品类的选中情况计算主品类的状态
  for (const mainGenre of result.genretor.mainGenres) {
    if (!mainGenre?.value) continue;

    const mainGenreOption = findOptionByValue(currentOptions, mainGenre.value);
    const totalSubOptions = mainGenreOption?.children?.length || 0;
    const matchedSubGenres = finalSubGenres.filter((sg) => sg?.parentValue === mainGenre.value);

    if (matchedSubGenres.length === 0) {
      // 没有子品类被选中
      mainGenre.checked = false;
      mainGenre.indeterminate = false;
    } else if (matchedSubGenres.length === totalSubOptions && totalSubOptions > 0) {
      // 所有子品类都被选中（全选）
      mainGenre.checked = true;
      mainGenre.indeterminate = false;
    } else {
      // 部分子品类被选中（部分选中）
      mainGenre.checked = false;
      mainGenre.indeterminate = true;
    }
  }

  return result;
};

/**
 * 解析 Game 筛选器参数（异步获取游戏详情）
 * @param params - URL query 参数对象
 * @returns Promise<void>
 */
const parseGameFilterParamsOriginal = (params: IRecord): IRecord => {
  const gameIds = parseArrayParam(params?.game_ids);

  if (isEmpty(gameIds)) {
    return {};
  }

  return {
    allGames: {
      value: gameIds,
      keywords: '',
      isSearchAll: true,
      checkedList: [],
    },
  };
};

/**
 * 解析 KPI 指标筛选器参数
 * 根据 URL 中的 kpi_metric_type 或 kpi_metric_dataSource 参数，构建完整的指标筛选器值
 *
 * @param params - URL query 参数对象
 * @returns KPI 指标筛选器参数对象
 *
 * @example
 * // 输入: { kpi_metric_type: 'revenue' }
 * // 输出: { metric_selection_for_trends: { value: { ... } } }
 *
 * @example
 * // 输入: { kpi_metric_dataSource: 'mscience' }
 * // 输出: { metric_selection_for_trends: { value: { ... } } }
 */
/**
 * 从候选列表中解析单值的快捷选择值。
 * 仅当 values 恰好包含一个元素，且该元素是列表中的合法非 'all' 值时，
 * 返回该值；否则返回 'all'。
 */
const resolveShotSelectValue = (values: string[], list: { value: string }[]): string => {
  if (values.length !== 1) {
    return 'all';
  }
  const validValues = list.map((item) => item.value).filter((v) => v !== 'all');
  return validValues.includes(values[0]) ? values[0] : 'all';
};

const parseKpiMetricsUrlFilterParamsOriginal = (params: IRecord): IRecord => {
  const result: IRecord = {};

  // 提取指标类型或数据源参数
  const metricType = params?.kpi_metric_type;
  const metricDataSource = params?.kpi_metric_dataSource;

  // 获取时间类型（默认为 monthly）
  const dateType = params?.granularity || 'monthly';

  // 优先处理 kpi_metric_type
  if (metricType) {
    let parsedMetricTypes: string[];

    // 判断是否为多个指标类型
    if (typeof metricType === 'string' && metricType.includes(',')) {
      parsedMetricTypes = parseArrayParam(metricType);
    } else if (typeof metricType === 'string') {
      parsedMetricTypes = [metricType];
    } else {
      parsedMetricTypes = [];
    }

    if (parsedMetricTypes.length > 0) {
      // 找到这些类型下所有的指标（考虑时间粒度）
      const allMetricsOfType: string[] = [];
      parsedMetricTypes.forEach((type) => {
        const metrics = allMetricsOptions
          .filter((option) => option?.metricsType === type && option?.dateEnum?.includes(dateType))
          .map((option) => option?.value);
        allMetricsOfType.push(...metrics);
      });

      if (allMetricsOfType.length > 0) {
        const sourceMetrics = getSourceMetricsByValue(allMetricsOfType);
        const metricsOptions = getMetricOptionByValue(allMetricsOfType);

        result[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND] = {
          value: {
            type: dateType, // 时间类型
            types: parsedMetricTypes.length === 1 ? parsedMetricTypes[0] : 'all', // 单个或全部
            metrics: allMetricsOfType, // 选中的指标列表
            sources: ['all'], // 所有数据源
            source_metrics: sourceMetrics, // 按数据源分组的指标
            metricsOptions, // 指标选项
            selectedMetricsValue: 'metrics', // 当前选中模式为指标
            shotSelectValue: resolveShotSelectValue(parsedMetricTypes, defaultMetricTypeList), // 快捷选择值
            selectCutVal: 'all', // 选择切片值
          },
        };
      }
    }
  }
  // 如果没有 kpi_metric_type，则处理 kpi_metric_dataSource
  else if (metricDataSource) {
    let parsedDataSources: string[];

    // 判断是否为多个数据源
    if (typeof metricDataSource === 'string' && metricDataSource.includes(',')) {
      parsedDataSources = parseArrayParam(metricDataSource);
    } else if (typeof metricDataSource === 'string') {
      parsedDataSources = [metricDataSource];
    } else {
      parsedDataSources = [];
    }

    if (parsedDataSources.length > 0) {
      // 找到这些数据源下所有的指标（考虑时间粒度）
      const allMetricsOfDataSource: string[] = [];
      parsedDataSources.forEach((source) => {
        const metrics = allMetricsOptions
          .filter((option) => option?.dataSource === source && option?.dateEnum?.includes(dateType))
          .map((option) => option.value);
        allMetricsOfDataSource.push(...metrics);
      });

      if (allMetricsOfDataSource.length > 0) {
        const sourceMetrics = getSourceMetricsByValue(allMetricsOfDataSource);
        const metricsOptions = getMetricOptionByValue(allMetricsOfDataSource);

        result[filterKeyMap.METRIC_SELECT_FOR_KPI_TREND] = {
          value: {
            type: dateType, // 时间类型
            types: 'all', // 所有指标类型
            metrics: allMetricsOfDataSource, // 选中的指标列表
            sources: parsedDataSources.length === 1 ? parsedDataSources : ['all'], // 数据源列表
            source_metrics: sourceMetrics, // 按数据源分组的指标
            metricsOptions, // 指标选项
            selectedMetricsValue: 'dataSource', // 当前选中模式为数据源
            shotSelectValue: resolveShotSelectValue(parsedDataSources, defaultDataSourceList), // 快捷选择值
            selectCutVal: 'all', // 选择切片值
          },
        };
      }
    }
  }

  return result;
};

const parseRangeUrlFilterParams = buildSilenceExecutor(parseRangeUrlFilterParamsOriginal);
const parseRangeApiParams = buildSilenceExecutor(parseRangeApiParamsOriginal);
const parseOrderUrlParams = buildSilenceExecutor(parseOrderUrlParamsOriginal);
const parseDateRangeUrlFilterParams = buildSilenceExecutor(parseDateRangeUrlFilterParamsOriginal);
const parseRegionUrlFilterParams = buildSilenceExecutor(parseRegionUrlFilterParamsOriginal);
const parseGenreUrlFilterParams = buildSilenceExecutor(parseGenreUrlFilterParamsOriginal);
const parsePlatformUrlFilterParams = buildSilenceExecutor(parsePlatformUrlFilterParamsOriginal);
const parseCascaderPlatformUrlFilterParams = buildSilenceExecutor(parseCascaderPlatformUrlFilterParamsOriginal);
const parseCompanyUrlFilterParams = buildSilenceExecutor(parseCompanyUrlFilterParamsOriginal);
const parseTagUrlFilterParams = buildSilenceExecutor(parseTagUrlFilterParamsOriginal);
const parseGrowthUrlFilterParams = buildSilenceExecutor(parseGrowthUrlFilterParamsOriginal);
const parseGameEngineUrlFilterParams = buildSilenceExecutor(parseGameEngineUrlFilterParamsOriginal);
const parseGameEngineApiParams = buildSilenceExecutor(parseGameEngineApiParamsOriginal);
const parseSteamTagsUrlFilterParams = buildSilenceExecutor(parseSteamTagsUrlFilterParamsOriginal);
const parseSteamTagsApiParams = buildSilenceExecutor(parseSteamTagsApiParamsOriginal);
const parseGameModeUrlFilterParams = buildSilenceExecutor(parseGameModeUrlFilterParamsOriginal);
const parseMobileDataSourceUrlFilterParams = buildSilenceExecutor(parseMobileDataSourceUrlFilterParamsOriginal);
const parseRevenueScopeUrlFilterParams = buildSilenceExecutor(parseRevenueScopeUrlFilterParamsOriginal);
const parseRevenueScopeApiParams = buildSilenceExecutor(parseRevenueScopeApiParamsOriginal);
const parseGSDPublisherSharedUrlFilterParams = buildSilenceExecutor(parseGSDPublisherSharedUrlFilterParamsOriginal);
const parseSegmentUrlFilterParams = buildSilenceExecutor(parseSegmentUrlFilterParamsOriginal);
const parseReleaseTimeUrlFilterParams = buildSilenceExecutor(parseReleaseTimeUrlFilterParamsOriginal);
const handleMetricRangeFilter = buildSilenceExecutor(handleMetricRangeFilterOriginal);
const parseGameFilterParams = buildSilenceExecutor(parseGameFilterParamsOriginal);
const parseKpiMetricsUrlFilterParams = buildSilenceExecutor(parseKpiMetricsUrlFilterParamsOriginal);
const parseRetentionUrlFilterParams = buildSilenceExecutor(parseRetentionUrlFilterParamsOriginal);

/**
 * 统一入口：解析 URL 筛选器参数
 * 当 URL 中存在 use_url_filter 标志时，执行新的解析逻辑
 * @param params - URL query 参数对象
 * @param options - 其他参数
 * @param options.genreOptionsMap - 品类选项数据map
 * @returns 解析后的筛选器参数对象和自定义参数
 */
export const parseUrlFilterParams = (
  params: IRecord,
  options: {
    genreOptionsMap?: IRecord<IOptionWithChildren[]>;
  },
): {
  filterParams: IRecord;
  customParams: IRecord;
} => {
  const filterParams: IRecord = {};
  const customParams: IRecord = {};

  // 检查是否有 use_url_filter 标志
  if (!params?.use_url_filter) {
    return { filterParams, customParams };
  }

  // 解析 Range 筛选器参数（用于 Filter 组件显示）
  const rangeParams = parseRangeUrlFilterParams(params);
  Object.assign(filterParams, rangeParams);

  // 解析 Range 筛选器参数为 API 可用格式
  const rangeApiParams = parseRangeApiParams(params);
  Object.assign(customParams, rangeApiParams);

  // 解析排序参数
  const orderParams = parseOrderUrlParams(params);
  Object.assign(customParams, orderParams);

  // 解析日期筛选器参数（包括粒度和日期范围）
  // 替换原有的 parseGranularityParams 函数
  const dateRangeResult = parseDateRangeUrlFilterParams(params);
  Object.assign(filterParams, dateRangeResult.filterParams);
  Object.assign(customParams, dateRangeResult.customParams);

  // 解析地区国家参数
  const regionParams = parseRegionUrlFilterParams(params);
  Object.assign(filterParams, regionParams);

  // 解析品类参数
  const genreParams = parseGenreUrlFilterParams(params, options);

  // 将 genretor 对象转换为 API 需要的格式
  if (genreParams?.genretor && Object.keys(genreParams.genretor).length > 0) {
    const genreSelectValue = genreParams.genretor;
    filterParams[filterKeyMap.GENRETOR] = formatGenerePageFunc(
      {
        ...genreSelectValue,
        options: options?.genreOptionsMap?.[genreSelectValue?.source] || [],
      },
      '',
      { isStdGenreValuesHandler: true },
    );
  }

  // 解析 Platform 筛选器参数
  const platformParams = parsePlatformUrlFilterParams(params);
  Object.assign(filterParams, platformParams);

  // 解析 级联Platform 筛选器参数
  const cascaderPlatformParams = parseCascaderPlatformUrlFilterParams(params);
  Object.assign(filterParams, cascaderPlatformParams);

  // 解析 Company 筛选器参数
  const companyParams = parseCompanyUrlFilterParams(params);
  Object.assign(filterParams, companyParams);

  // 解析 Tag 筛选器参数（Product Model 和 App Magic Tags）
  const tagParams = parseTagUrlFilterParams(params);
  Object.assign(filterParams, tagParams);

  // 解析 Growth 类型筛选器参数
  const growthParams = parseGrowthUrlFilterParams(['revenue'], params);
  Object.assign(filterParams, growthParams);

  // 解析 Game Engine 筛选器参数
  const gameEngineParams = parseGameEngineUrlFilterParams(params);
  Object.assign(filterParams, gameEngineParams);

  // 解析 Game Engine 筛选器参数为 API 可用格式
  const gameEngineApiParams = parseGameEngineApiParams(params);
  Object.assign(customParams, gameEngineApiParams);

  // 解析 Steam Tags 筛选器参数
  const steamTagsParams = parseSteamTagsUrlFilterParams(params);
  Object.assign(filterParams, steamTagsParams);

  // 解析 Steam Tags 筛选器参数为 API 可用格式
  const steamTagsApiParams = parseSteamTagsApiParams(params);
  Object.assign(customParams, steamTagsApiParams);

  // 解析 Game Mode 筛选器参数
  const gameModeParams = parseGameModeUrlFilterParams(params);
  Object.assign(filterParams, gameModeParams);

  // 解析 DataSource（单选）筛选器参数
  const dataSourceParams = parseMobileDataSourceUrlFilterParams(params);
  Object.assign(filterParams, dataSourceParams);

  // 解析收入范围筛选器参数
  const revenueScopeParams = parseRevenueScopeUrlFilterParams(params);
  Object.assign(filterParams, revenueScopeParams);

  // 解析收入范围筛选器参数为 API 可用格式
  const revenueScopeApiParams = parseRevenueScopeApiParams(params);
  Object.assign(customParams, revenueScopeApiParams);

  // 解析 GSD Publisher Shared 筛选器参数
  const gsdPublisherSharedParams = parseGSDPublisherSharedUrlFilterParams(params);
  Object.assign(filterParams, gsdPublisherSharedParams);

  // 解析 Segment for Sales 筛选器参数
  const segmentParams = parseSegmentUrlFilterParams(params);
  Object.assign(filterParams, segmentParams);

  // 解析发布时间筛选器参数
  const releaseTimeResult = parseReleaseTimeUrlFilterParams(params);
  Object.assign(filterParams, releaseTimeResult.filterParams);
  Object.assign(customParams, releaseTimeResult.customParams);

  // 处理 Range 筛选器与其他筛选器的兼容
  Object.assign(filterParams, handleMetricRangeFilter(filterParams));

  // // 解析 Game 筛选器参数
  const gameParams = parseGameFilterParams(params);
  Object.assign(filterParams, gameParams);
  Object.assign(customParams, {
    filter_ids: gameParams?.allGames?.value || [],
    entityTypesIdsObj: {
      mobile: gameParams?.allGames?.value || [],
    },
  });

  // 解析 KPI 指标筛选器参数
  const kpiMetricsParams = parseKpiMetricsUrlFilterParams(params);
  Object.assign(filterParams, kpiMetricsParams);

  // 解析 Retention 筛选器参数
  const retentionParams = parseRetentionUrlFilterParams(params);
  Object.assign(filterParams, retentionParams);

  return { filterParams, customParams };
};
